import React, { useCallback, useState, useMemo, Fragment } from 'react';
import { useHistory } from 'react-router-dom';

import Header from 'components/AppBar';
import Pagination from 'components/Pagination';
import { Mobile, Desktop } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import Shimmer from 'components/Shimmer';
import { SearchParamsHistory, SearchTypesHistory } from 'components/TripSearch/models';
import TripSearchContainer from 'containers/TripSearch';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { RequestedItem } from 'models/requestHistory';
import ErrorMessage from 'screens/components/RequestHistory/components/ErrorMessage';
import ListView from 'screens/components/RequestHistory/components/listView';
import OptionTab from 'screens/components/RequestHistory/components/OptionTab';
import FilterTags from 'screens/components/TravelRequested/components/FilterTags';
import { checkFilterTagsMapper, CheckBy } from 'utils/filterTags';

import { Wrapper, SearchButton, IconSearch, ButtonsContainer, TabContainer, FilterTagsContainer } from './styles';
import FilterButton from '../Dashboard/components/FilterButton';

interface Props {
  data?: RequestedItem[];
  isLoading: boolean;
  loaded: boolean;
  setPayload: (searchParams: SearchParamsHistory) => void;
  total: number;
  maxItemsPerPage: number;
  page: number;
  params: SearchParamsHistory;
  error?: string;
}

const RequestHistory = ({
  data,
  isLoading,
  loaded,
  setPayload,
  total,
  maxItemsPerPage,
  params,
  page,
  error,
}: Props) => {
  const [key, setKey] = useState<string>(SearchTypesHistory.week);
  const [showSearch, setShowSearch] = useState(false);
  const { t } = useStoreTranslation();
  const history = useHistory();
  const DEFAULT_INIT_DATE = undefined;
  const DEFAULT_END_DATE = undefined;

  const handleChange = (value: string) => {
    const newParams = { type: value === 'week' ? SearchTypesHistory.week : SearchTypesHistory.month, page: 1 };
    setKey(value);
    setPayload(newParams);
  };
  const handleClose = () => history.push('/corporate/dashboard');

  const updateParams = useCallback(
    (newParams: SearchParamsHistory) => {
      setKey(newParams.type as string);
      setPayload(newParams);
    },
    [setPayload],
  );

  const handlePageChange = useCallback(
    (p: number) => {
      updateParams({ ...params, page: p });
    },
    [updateParams, params],
  );

  const LoadingButtons = () => (
    <ButtonsContainer>
      <TabContainer>
        {Number(2)
          .toArray()
          .map((i) => (
            <OptionTab
              key={i}
              label={<Shimmer>Option</Shimmer>}
              isActive={false}
              onClick={() => {}}
              testId="test-id-loading"
            />
          ))}
      </TabContainer>
      <SearchButton onClick={() => {}}>
        <IconSearch isLoading={true} />
      </SearchButton>
    </ButtonsContainer>
  );

  const tabs = [
    {
      key: SearchTypesHistory.week,
      label: t('requestHistory.weekOption'),
      value: 'week',
      testId: 'req-history-week-button',
    },
    {
      key: SearchTypesHistory.month,
      label: t('requestHistory.monthOption'),
      value: 'month',
      testId: 'req-history-month-button',
    },
  ];

  const checkFilterTags = useCallback(
    (searchParams?: SearchParamsHistory) =>
      checkFilterTagsMapper(DEFAULT_INIT_DATE, DEFAULT_END_DATE, CheckBy.SearchTypesHistory, searchParams),
    [DEFAULT_INIT_DATE, DEFAULT_END_DATE],
  );

  const hasTags = useMemo(
    () => loaded && params.type !== SearchTypesHistory.week && params.type !== SearchTypesHistory.month,
    [loaded, params.type],
  );

  return (
    <Wrapper>
      <Mobile>
        <Header noGoBack rightIcon="/corporate/images/close.svg" rightAction={handleClose}>
          {t('requestHistory.title')}
        </Header>
      </Mobile>
      <Desktop>
        <ScreenHeader
            title={t('requestHistory.title')}
            right={<FilterButton isHistorial={true} />}
          />
      </Desktop>
      <Fragment>
        {!isLoading && loaded ? (
          <ButtonsContainer>
            <TabContainer>
              {tabs.map((tab) => (
                <OptionTab
                  key={tab.key}
                  onClick={() => handleChange(tab.value)}
                  isActive={key === tab.key}
                  label={tab.label}
                  testId={tab.testId}
                />
              ))}
            </TabContainer>
            <SearchButton onClick={() => setShowSearch(true)}>
              <IconSearch isLoading={false} />
            </SearchButton>
          </ButtonsContainer>
        ) : (
          LoadingButtons()
        )}
      </Fragment>

      <Fragment>
        {!isLoading && hasTags && (
          <FilterTagsContainer>
            <FilterTags
              params={params ?? {}}
              onChange={(params) => {
                const newParams = checkFilterTags(params)
                  ? { ...params, page: 1, type: SearchTypesHistory.search }
                  : { page: 1, type: SearchTypesHistory.week };
                updateParams(newParams);
              }}
            />
          </FilterTagsContainer>
        )}
      </Fragment>

      {error ? (
        <Fragment>
          <ErrorMessage title="" subtitle={error} />
        </Fragment>
      ) : (
        <Fragment>
          <ListView data={data} isLoading={isLoading} hasFilterTags={checkFilterTags(params)} />
          {loaded && total > 0 && !isLoading && (
            <Pagination currentPage={page} maxItemsPerPage={maxItemsPerPage} total={total} onClick={handlePageChange} />
          )}
        </Fragment>
      )}

      {loaded && (
        <TripSearchContainer
          show={showSearch}
          onClose={() => setShowSearch(false)}
          onSearch={(params: SearchParamsHistory) => {
            setShowSearch(false);
            const newParams: SearchParamsHistory = { ...params, page: 1, type: SearchTypesHistory.search };
            updateParams(newParams);
          }}
          params={params}
          withItineraryProps={true}
        />
      )}
    </Wrapper>
  );
};

export default RequestHistory;
